import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEquipmentSelection } from "../../hooks/useEquipmentSelection";
import { useUpdateLab } from "../../hooks/useUpdateLab";
import type { CreateLabRequest } from "../../types";
import type { Equipment } from "../../../EquipmentsTab/types/types";
import type { Lab } from "../../types";
import { createLabSchema } from "../../schema";
import {
	EquipmentDisplay,
	EquipmentSelector,
	LoadingState,
	ErrorState,
	ActionButtons,
	LabNameField,
	LabDescriptionField,
} from "./components";
import FormFieldLabel from "@components/common/LabeledField/FormFieldLabel";

type CreateLabFormData = z.infer<typeof createLabSchema>;

interface CreateLabFormProps {
	onSubmit: (data: CreateLabRequest) => void;
	onCancel: () => void;
	isSubmitting: boolean;
	initialValues?: Lab;
	isEdit?: boolean;
	isView?: boolean;
}

const CreateLabForm: React.FC<CreateLabFormProps> = ({
	onSubmit,
	onCancel,
	isSubmitting,
	initialValues,
	isEdit = false,
	isView = false,
}) => {
	const [hoveredChipIndex, setHoveredChipIndex] = useState<number | null>(null);
	const isReadOnly = isView;

	// Initialize equipment selection when editing or viewing
	const initialEquipments =
		initialValues && (isEdit || isView)
			? initialValues.labEquipments?.map(item => ({
					equipment: {
						...item?.equipment,
						id: String(item?.equipment?.id),
						uuid: item?.equipment?.uuid,
						createdAt: item?.equipment?.createdAt,
						updatedAt: item?.equipment?.updatedAt,
					} as Equipment,
					count: item?.requiredCount,
			  }))
			: [];

	// Use custom hook for equipment selection (now handles its own data fetching)
	const {
		selectedEquipments,
		selectedEquipmentId,
		equipmentCount,
		editingEquipmentIndex,
		visibleEquipmentOptions,
		hasMoreOptions,
		isFetchingNextPage,
		isInitialEquipmentLoading,
		isEquipmentSearchLoading,
		showInitialEquipmentError,
		error,
		handleSelectEquipmentId,
		setEquipmentCount,
		handleAddEquipment,
		handleStartEdit,
		handleSaveEdit,
		handleCancelEdit,
		handleRemoveEquipment,
		loadMoreOptions,
		resetEquipmentSelection,
		setEquipmentSearch,
		canAddEquipment,
		canConfirmAction,
		totalEquipmentCount,
	} = useEquipmentSelection({
		initialEquipments,
	});

	// Use update lab hook for edit mode
	// NOTE: Error toasts are handled globally in the mutation's onError,
	// so we only close the modal on success here to avoid duplicate toasts.
	const { updateLab, isUpdating } = useUpdateLab({
		onSuccess: () => {
			onCancel(); // Close modal on success
		},
		onError: error => {
			// Keep logging for debugging, but rely on the global error handler for user-facing toasts
			console.error("Update lab error:", error);
		},
	});

	const { control, reset, watch } = useForm<CreateLabFormData>({
		resolver: zodResolver(createLabSchema),
		mode: "onChange",
		defaultValues: {
			name: initialValues?.name || "",
			description: initialValues?.description || "",
			equipments: [],
		},
	});

	const nameValue = watch("name");
	const descriptionValue = watch("description");

	// Validate the name directly against the schema instead of relying on RHF's
	// `isValid`, which does not react to equipment changes (they live in a separate hook).
	const isNameValid = createLabSchema.shape.name.safeParse(nameValue).success;

	// Check if form is valid with proper null checks and form validation
	const isFormValid = Boolean(
		isNameValid &&
			selectedEquipments.length > 0 &&
			selectedEquipments.every(item => item && typeof item.count === "number" && item.count > 0),
	);

	const handleCancel = () => {
		reset();
		resetEquipmentSelection();
		onCancel();
	};

	// Handle form submission for both create and update
	const handleSubmit = () => {
		if (isReadOnly || !isFormValid || isSubmitting || isUpdating) return;
		if (isEdit && initialValues) {
			updateLab(initialValues, nameValue || "", descriptionValue || "", selectedEquipments);
		} else {
			const formData = {
				name: nameValue || "",
				description: descriptionValue?.trim() || undefined,
				labEquipments: selectedEquipments.map(item => ({
					equipmentId: item.equipment.id,
					requiredCount: item.count,
				})),
			};

			onSubmit(formData);
		}
	};

	const fieldsDisabled = isSubmitting || isUpdating || isReadOnly;

	// Full-form loader only on first equipment fetch (not when searching in the dropdown)
	if (isInitialEquipmentLoading) {
		return <LoadingState />;
	}

	if (showInitialEquipmentError && error) {
		return <ErrorState error={error} />;
	}

	return (
		<div className="space-y-6">
			{/* Lab Name */}
			<LabNameField control={control} isSubmitting={fieldsDisabled} />

			{/* Lab Description */}
			<LabDescriptionField control={control} isSubmitting={fieldsDisabled} />

			{/* Equipment Selection */}
			<div className="space-y-4">
				{!isReadOnly && (
					<>
						<FormFieldLabel required>{isEdit ? "Update equipment" : "Add equipment"}</FormFieldLabel>

						<EquipmentSelector
							selectedEquipmentId={selectedEquipmentId}
							equipmentCount={equipmentCount}
							editingEquipmentIndex={editingEquipmentIndex}
							visibleEquipmentOptions={visibleEquipmentOptions}
							hasMoreOptions={hasMoreOptions}
							isEquipmentSearchLoading={isEquipmentSearchLoading}
							isFetchingNextPage={isFetchingNextPage}
							isSubmitting={fieldsDisabled}
							canAddEquipment={canAddEquipment}
							canConfirmAction={canConfirmAction}
							onEquipmentSelect={handleSelectEquipmentId}
							onCountChange={setEquipmentCount}
							onAddEquipment={handleAddEquipment}
							onSaveEdit={handleSaveEdit}
							onCancelEdit={handleCancelEdit}
							onLoadMoreOptions={loadMoreOptions}
							onEquipmentSearchChange={setEquipmentSearch}
						/>
					</>
				)}

				{/* Equipment Display Section */}
				<EquipmentDisplay
					selectedEquipments={selectedEquipments}
					editingEquipmentIndex={isReadOnly ? null : editingEquipmentIndex}
					hoveredChipIndex={isReadOnly ? null : hoveredChipIndex}
					isSubmitting={fieldsDisabled}
					totalEquipmentCount={totalEquipmentCount}
					canAddEquipment={isReadOnly ? true : canAddEquipment}
					onMouseEnter={setHoveredChipIndex}
					onMouseLeave={() => setHoveredChipIndex(null)}
					onStartEdit={handleStartEdit}
					onRemoveEquipment={handleRemoveEquipment}
				/>
			</div>

			{/* Action Buttons */}
			<ActionButtons
				isEdit={isEdit}
				isView={isView}
				isFormValid={isFormValid}
				isSubmitting={isSubmitting || isUpdating}
				onCancel={handleCancel}
				onSubmit={handleSubmit}
			/>
		</div>
	);
};

export default CreateLabForm;
